import { useEffect, useRef, useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { TextSelection } from '@tiptap/pm/state';
import { Bold, Check, Code, Italic, Link2, Strikethrough, X } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { IconButton } from '../icon-button/icon-button.tsx';
import { BlockHandle } from './block-handle.tsx';
import {
  DEFAULT_UPLOAD_RULES,
  insertFiles,
  setAttachmentContext,
  upgradeAttachmentLinks,
  type RichTextEditorUploadRules,
  type UploadFn,
} from './attachments.tsx';
import { buildEditorExtensions } from './extensions.ts';
import { buildSlashItems, setSlashItems } from './slash-menu.tsx';
import type { RichTextEditorLabels } from './labels.ts';

const editorVariants = cva('w-full', {
  variants: {
    variant: {
      quiet:
        'rounded-md border border-transparent bg-fill-faint px-3.25 py-2.75 transition focus-within:shadow-focus',
      bare: '',
    },
  },
  defaultVariants: { variant: 'quiet' },
});

/*
 * The document's typography, addressed through the ProseMirror element the way
 * chat-markdown addresses its rendered HTML. Same tokens, so what is written
 * here reads the same once it is rendered read-only elsewhere.
 */
const contentClass = cn(
  'outline-none text-base leading-relaxed text-ink font-medium whitespace-pre-wrap break-words',
  '[&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
  '[&_h1]:text-lg [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-1 [&_h1:first-child]:mt-0',
  '[&_h2]:text-md [&_h2]:font-bold [&_h2]:mt-3.5 [&_h2]:mb-1 [&_h2:first-child]:mt-0',
  '[&_h3]:text-base [&_h3]:font-bold [&_h3]:mt-3 [&_h3]:mb-0.5 [&_h3:first-child]:mt-0',
  '[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5',
  '[&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:my-0.5 [&_li>p]:my-0',
  '[&_ul[data-type=taskList]]:list-none [&_ul[data-type=taskList]]:pl-0',
  '[&_ul[data-type=taskList]_li]:flex [&_ul[data-type=taskList]_li]:items-start [&_ul[data-type=taskList]_li]:gap-2',
  '[&_ul[data-type=taskList]_li>label]:mt-1 [&_ul[data-type=taskList]_li>label]:flex [&_ul[data-type=taskList]_li>label]:shrink-0',
  '[&_ul[data-type=taskList]_li>div]:min-w-0 [&_ul[data-type=taskList]_li>div]:flex-1',
  '[&_ul[data-type=taskList]_input]:accent-primary [&_ul[data-type=taskList]_input]:size-3.5 [&_ul[data-type=taskList]_input]:cursor-pointer',
  '[&_li[data-checked=true]>div]:text-ink-muted [&_li[data-checked=true]>div]:line-through',
  '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-line [&_blockquote]:pl-3 [&_blockquote]:text-ink-secondary',
  '[&_hr]:my-4 [&_hr]:border-t [&_hr]:border-line',
  '[&_code]:rounded-xs [&_code]:bg-fill [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-sm [&_code]:font-mono',
  '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-sm [&_pre]:bg-fill [&_pre]:p-3 [&_pre]:text-sm [&_pre]:font-mono',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_a]:underline [&_a]:underline-offset-2',
  '[&_img]:my-2 [&_img]:max-w-full [&_img]:rounded-md',
  '[&_.ProseMirror-selectednode]:outline-2 [&_.ProseMirror-selectednode]:outline-offset-2 [&_.ProseMirror-selectednode]:outline-ink-faint',
  '[&_p.is-editor-empty:first-child]:before:pointer-events-none [&_p.is-editor-empty:first-child]:before:float-left',
  '[&_p.is-editor-empty:first-child]:before:h-0 [&_p.is-editor-empty:first-child]:before:text-ink-muted',
  '[&_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)]',
);

export interface RichTextEditorProps extends VariantProps<typeof editorVariants> {
  /*
   * The saved markdown. Applied on mount, and re-applied when it changes while
   * the editor is not focused — a refetch or a regenerate lands, typing never
   * gets yanked away.
   */
  value: string;
  /** Fires with the serialized markdown on every actual change. */
  onChange: (markdown: string) => void;
  labels: RichTextEditorLabels;
  ariaLabel: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Extra classes for the editable area itself, e.g. a minimum height. */
  contentClassName?: string;
  /** Wires paste/drop/insert of images and files. Absent = text only. */
  onUpload?: UploadFn;
  uploadRules?: RichTextEditorUploadRules;
  /** Which link hrefs are this app's attachments, shown as file chips. */
  isAttachmentHref?: (href: string) => boolean;
}

/**
 * The one text editor for documents: preparation, verslag, private notes.
 *
 * Markdown in, markdown out — the document IS the string the server stores
 * and the model reads. Everything the toolbar and slash menu offer survives
 * that round trip; nothing else is on offer.
 */
export function RichTextEditor({
  value,
  onChange,
  labels,
  ariaLabel,
  placeholder,
  disabled,
  variant,
  className,
  contentClassName,
  onUpload,
  uploadRules,
  isAttachmentHref,
}: RichTextEditorProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  const hrefTestRef = useRef(isAttachmentHref);
  const lastEmitted = useRef(value);

  useEffect(() => {
    onChangeRef.current = onChange;
    hrefTestRef.current = isAttachmentHref;
  });

  const editor = useEditor(
    {
      extensions: buildEditorExtensions({
        labels,
        placeholder,
        withUploads: onUpload !== undefined,
      }),
      content: value,
      contentType: 'markdown',
      editable: !disabled,
      editorProps: {
        attributes: {
          role: 'textbox',
          'aria-multiline': 'true',
          'aria-label': ariaLabel,
          class: cn(contentClass, contentClassName),
        },
      },
      onCreate: ({ editor: created }) => {
        const test = hrefTestRef.current;
        if (test) upgradeAttachmentLinks(created, test);
      },
      onUpdate: ({ editor: updated }) => {
        /* The serializer ends every document with a blank line; storage does not. */
        const markdown = updated.getMarkdown().trimEnd();
        if (markdown === lastEmitted.current) return;
        lastEmitted.current = markdown;
        onChangeRef.current(markdown);
      },
    },
    [],
  );

  /* The menu's items and the upload wiring live in editor storage; keep them current. */
  useEffect(() => {
    const rules = uploadRules ?? DEFAULT_UPLOAD_RULES;
    setSlashItems(
      editor,
      buildSlashItems(labels, {
        enabled: onUpload !== undefined,
        pick: (kind) => {
          const input = fileInput.current;
          if (!input) return;
          input.accept = (kind === 'image' ? rules.imageTypes : rules.fileTypes).join(
            ',',
          );
          input.click();
        },
      }),
    );
    setAttachmentContext(editor, onUpload ? { upload: onUpload, rules } : null);
  }, [editor, labels, onUpload, uploadRules]);

  useEffect(() => {
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    if (editor.isFocused) return;
    lastEmitted.current = value;
    editor.commands.setContent(value, { contentType: 'markdown', emitUpdate: false });
    const test = hrefTestRef.current;
    if (test) upgradeAttachmentLinks(editor, test);
  }, [editor, value]);

  const pickedFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    insertFiles(editor, Array.from(files), editor.state.selection.to);
  };

  return (
    <div
      data-slot='rich-text-editor'
      className={cn(editorVariants({ variant }), className)}
    >
      <EditorContent editor={editor} />
      <SelectionToolbar editor={editor} labels={labels} />
      {!disabled && <BlockHandle editor={editor} labels={labels} />}
      {onUpload !== undefined && (
        <input
          ref={fileInput}
          type='file'
          hidden
          tabIndex={-1}
          aria-hidden='true'
          onChange={(event) => {
            pickedFiles(event.currentTarget.files);
            event.currentTarget.value = '';
          }}
        />
      )}
    </div>
  );
}

type ToolbarEditor = NonNullable<ReturnType<typeof useEditor>>;

interface SelectionToolbarProps {
  editor: ToolbarEditor;
  labels: RichTextEditorLabels;
}

const toolbarButton = (active: boolean) => cn('rounded-sm', active && 'bg-fill text-ink');

/*
 * Appears over a text selection, Notion-style. Block changes live in the slash
 * menu; this is only for what applies to a run of selected text.
 */
function SelectionToolbar({ editor, labels }: SelectionToolbarProps) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const state = useEditorState({
    editor,
    selector: (context) => ({
      bold: context.editor.isActive('bold'),
      italic: context.editor.isActive('italic'),
      strike: context.editor.isActive('strike'),
      code: context.editor.isActive('code'),
      link: context.editor.isActive('link'),
      href: (context.editor.getAttributes('link').href as string | undefined) ?? '',
    }),
  });

  const applyLink = () => {
    const trimmed = draft.trim();
    const chain = editor.chain().focus().extendMarkRange('link');
    if (trimmed === '') {
      chain.unsetLink().run();
    } else {
      const href = /^(https?:\/\/|mailto:)/i.test(trimmed)
        ? trimmed
        : `https://${trimmed}`;
      chain.setLink({ href }).run();
    }
    setLinkOpen(false);
  };

  return (
    <BubbleMenu
      editor={editor}
      options={{ placement: 'top-start', offset: 8 }}
      shouldShow={({ editor: current, state: editorState }) => {
        if (!current.isEditable) return false;
        const { selection } = editorState;
        if (!(selection instanceof TextSelection) || selection.empty) return false;
        return !current.isActive('codeBlock');
      }}
    >
      <div
        data-slot='selection-toolbar'
        className='flex items-center gap-0.5 rounded-md border border-line bg-surface p-1 shadow-menu animate-pop'
      >
        {linkOpen ? (
          <>
            {/*
             * Not the Input component: this field lives inside a floating
             * toolbar and needs none of the form-field chrome. Autofocus is
             * right here — the field appears because the link button was
             * just clicked.
             */}
            <input
              autoFocus
              type='text'
              aria-label={labels.link}
              placeholder={labels.linkPlaceholder}
              value={draft}
              onChange={(event) => setDraft(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applyLink();
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  setLinkOpen(false);
                  editor.commands.focus();
                }
              }}
              className='w-56 bg-transparent px-2 text-sm font-medium text-ink outline-none placeholder:text-ink-muted'
            />
            <IconButton
              size='sm'
              shape='square'
              aria-label={labels.link}
              onClick={applyLink}
            >
              <Check className='size-3.5' aria-hidden='true' />
            </IconButton>
            {state.link && (
              <IconButton
                size='sm'
                shape='square'
                variant='danger'
                aria-label={labels.removeLink}
                onClick={() => {
                  editor.chain().focus().extendMarkRange('link').unsetLink().run();
                  setLinkOpen(false);
                }}
              >
                <X className='size-3.5' aria-hidden='true' />
              </IconButton>
            )}
          </>
        ) : (
          <>
            <IconButton
              size='sm'
              shape='square'
              aria-label={labels.bold}
              aria-pressed={state.bold}
              className={toolbarButton(state.bold)}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <Bold className='size-3.5' aria-hidden='true' />
            </IconButton>
            <IconButton
              size='sm'
              shape='square'
              aria-label={labels.italic}
              aria-pressed={state.italic}
              className={toolbarButton(state.italic)}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <Italic className='size-3.5' aria-hidden='true' />
            </IconButton>
            <IconButton
              size='sm'
              shape='square'
              aria-label={labels.strike}
              aria-pressed={state.strike}
              className={toolbarButton(state.strike)}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            >
              <Strikethrough className='size-3.5' aria-hidden='true' />
            </IconButton>
            <IconButton
              size='sm'
              shape='square'
              aria-label={labels.code}
              aria-pressed={state.code}
              className={toolbarButton(state.code)}
              onClick={() => editor.chain().focus().toggleCode().run()}
            >
              <Code className='size-3.5' aria-hidden='true' />
            </IconButton>
            <IconButton
              size='sm'
              shape='square'
              aria-label={labels.link}
              aria-pressed={state.link}
              className={toolbarButton(state.link)}
              onClick={() => {
                setDraft(state.href);
                setLinkOpen(true);
              }}
            >
              <Link2 className='size-3.5' aria-hidden='true' />
            </IconButton>
          </>
        )}
      </div>
    </BubbleMenu>
  );
}
